import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import FroalaEditor from 'common/FroalaEditor';
import { useModalManager } from 'common/Modal';
import { useScript } from 'common/Utils';
import { toCamelCase } from 'modules/TCIUtils';
import styles from '../../Slide.module.scss';
import './froala.scss';
import { slideObjectPropTypes } from '../../../Utils';
import { useDebouncedEffect } from '../../../../../common/Utils';
import useSlideShowStore from '../../../stores/useSlideShowStore';
import useSlideShowContext from '../../../stores/useSlideShowContext';
import { TEXT_VARIABLE } from '../../../../../shared/Froala/plugins/TextVariable';
import ElementInsertModal from './CustomFroalaCommands/ElementInsert/ElementInsertModal';
import { registerFroalaCommands } from './CustomFroalaCommands/FroalaListeners';
import CustomImageModal from './CustomFroalaCommands/CustomImage/CustomImageModal';
import CustomVideoModal from './CustomFroalaCommands/CustomVideo/CustomVideoModal';
import CustomAudioModal from './CustomFroalaCommands/CustomAudio/CustomAudioModal';
import TextEditorConfig from './EditorConfig';
import AnchoredDropdown from './AnchoredDropdown';
import useRetroactiveAdder from './useRetroactiveAdder';
import EquationInsert from './CustomFroalaCommands/EquationInsert';
import CustomEditImageModal from './CustomFroalaCommands/CustomImage/CustomEditImageModal';
import { useHtmlVariableListener } from './useHtmlVariableListener';

registerFroalaCommands();

const SlideObjectTextEditor = ({
  slideObject, updateSlideObject, hasError, setHasError, setIsSaving, isSaving,
}) => {
  const elementInsertModalManager = useModalManager();
  const addImageModalManager = useModalManager();
  const editImageModalManager = useModalManager();
  const addVideoModalManager = useModalManager();
  const audioModalManager = useModalManager();
  const equationModalManager = useModalManager();
  const [editorInstance, setEditorInstance] = useState(null);
  const [elementParent, setElementParent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [slideSoundClipId, setSlideSoundClipId] = useState('');
  const [$audioButtonToEdit, setAudioButtonToEdit] = useState(null);
  const [content, setContent] = useState(slideObject.html || '');
  const [pastedImage, setPastedImage] = useState(null);
  const [htmlElementId, setHtmlElementId] = useState();
  const [imageModel, setImageModel] = useState(null);

  const setHasUnsavedChanges = useSlideShowStore(state => state.setHasUnsavedChanges);
  const locale = useSlideShowContext(state => state.locale);
  const userType = useSlideShowContext(state => state.userType);

  const textVariableRef = useRef(null);

  useScript('https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js', {}, () => {
    window.MathJax = {
      loader: {
        load: ['input/asciimath', 'output/svg'],
      },
    };
  });

  useRetroactiveAdder(editorInstance);

  const activePlugins = [TEXT_VARIABLE.commandName];
  const pluginProps = { [TEXT_VARIABLE.commandName]: { textVariableRef } };

  const froalaConfig = TextEditorConfig({
    activePlugins,
    addImageModalManager,
    addVideoModalManager,
    audioModalManager,
    editImageModalManager,
    elementInsertModalManager,
    equationModalManager,
    pluginProps,
    setAudioButtonToEdit,
    setEditMode,
    setEditorInstance,
    setElementParent,
    setHasUnsavedChanges,
    setHtmlElementId,
    setImageModel,
    setPastedImage,
    setSlideSoundClipId,
    slideObject,
    userType,
  });

  useDebouncedEffect(() => {
    updateSlideObject({ html: content, locale });
  }, [content], 800);

  // replace editor content with locale specific content when changing locales
  useEffect(() => {
    if (!editorInstance || !slideObject || !locale) return;

    const slideObjectHtml = locale === 'en' ? slideObject.htmlEn : slideObject.htmlEs;

    editorInstance.html.set(slideObjectHtml);
    editorInstance.undo.saveStep();
  }, [editorInstance, locale]);

  useHtmlVariableListener(editorInstance);

  const anchoredDropdown = () => {
    if (!elementParent) return null;

    if (elementInsertModalManager.isOpen) return null;

    let button = slideObject.htmlElements.find(htmlElement => parseInt(htmlElement.id, 10) === parseInt(htmlElementId, 10));
    if (!button) return null;

    button = toCamelCase(button);

    if ([
      'ModelNumber',
      'ModelTitle',
      'ModelPhenomenon',
      'ModelStoryline',
      'ModelEssentialQuestion',
    ].includes(button.type)) return null;

    return (
      <AnchoredDropdown
        button={button}
        editorInstance={editorInstance}
        element={elementParent}
        isSaving={isSaving}
        openerClass="btn"
        openModal={elementInsertModalManager.open}
        setEditMode={setEditMode}
      />
    );
  };

  const customVideoModal = (localEditorInstance, modalManager, fromButton) => (
    <CustomVideoModal
      editorInstance={localEditorInstance}
      fromButton={fromButton}
      modalManager={modalManager}
      model={slideObject}
      modelType="SlideObject"
      userType={userType}
    />
  );

  const customImageModal = (localEditorInstance, modalManager, fromButton) => (
    <CustomImageModal
      clearDefaultImage={() => setPastedImage(null)}
      editorInstance={localEditorInstance}
      fromButton={fromButton}
      modalManager={modalManager}
      pastedImage={pastedImage}
      slideObject={slideObject}
    />
  );

  const editImageModal = (localEditorInstance, modalManager) => (
    <CustomEditImageModal
      editorInstance={localEditorInstance}
      imageModelToEdit={imageModel}
      modalManager={modalManager}
      slideObject={slideObject}
    />
  );

  const customAudioModal = (localEditorInstance, modalManager, localAudioButtonToEdit, setLocalAudioButtonToEdit, setLocalSlideSoundClipId, slideLocalSoundClipId) => (
    <CustomAudioModal
      $audioButtonToEdit={localAudioButtonToEdit}
      editorInstance={localEditorInstance}
      modalManager={modalManager}
      setAudioButtonToEdit={setLocalAudioButtonToEdit}
      setSlideSoundClipId={setLocalSlideSoundClipId}
      slideObject={slideObject}
      slideSoundClipId={slideLocalSoundClipId}
      userType={userType}
    />
  );

  const equationModal = (localEditorInstance, modalManager, fromButton) => (
    <EquationInsert
      editorInstance={localEditorInstance}
      fromButton={fromButton}
      modalManager={modalManager}
    />
  );

  const elementInsertModal = () => (
    <ElementInsertModal
      customAudioModal={customAudioModal}
      customImageModal={customImageModal}
      customVideoModal={customVideoModal}
      editMode={editMode}
      editorInstance={editorInstance}
      elementParent={elementParent}
      equationModal={equationModal}
      hasError={hasError}
      htmlElementId={htmlElementId}
      modalManager={elementInsertModalManager}
      setEditMode={setEditMode}
      setHasError={setHasError}
      setHtmlElementId={setHtmlElementId}
      setIsSaving={setIsSaving}
      slideObject={slideObject}
    />
  );

  return (
    <Fragment>
      <section
        aria-label={`Text Object ${slideObject.id}`}
        className={styles.textEditContainer}
        id={slideObject.id}
      >
        <FroalaEditor
          key={`${slideObject.id}-${locale}`}
          customConfig={{ ...froalaConfig, locale: locale }}
          model={content}
          tag="div"
          text={content}
          onModelChange={newContent => setContent(newContent)}
        />
      </section>

      {anchoredDropdown()}
      {elementInsertModalManager.isOpen && elementInsertModal()}
      {customVideoModal(editorInstance, addVideoModalManager, false)}
      {equationModal(editorInstance, equationModalManager, false)}
      {customImageModal(editorInstance, addImageModalManager, false)}
      {editImageModal(editorInstance, editImageModalManager, false)}
      {audioModalManager.isOpen && (
        customAudioModal(
          editorInstance,
          audioModalManager,
          $audioButtonToEdit,
          setAudioButtonToEdit,
          setSlideSoundClipId,
          slideSoundClipId
        )
      )}

    </Fragment>
  );
};

SlideObjectTextEditor.propTypes = {
  hasError: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  setHasError: PropTypes.func.isRequired,
  setIsSaving: PropTypes.func.isRequired,
  slideObject: slideObjectPropTypes.isRequired,
  updateSlideObject: PropTypes.func.isRequired,
};

export default SlideObjectTextEditor;
